import { useEffect, useRef } from "preact/hooks";
import type { ComponentChildren } from "preact";
import type { ChatMessage } from "../lib/types";

type MessageListProps = {
  messages: ChatMessage[];
  loading: boolean;
};

export default function MessageList({ messages, loading }: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    containerRef.current?.scrollTo({
      top: containerRef.current.scrollHeight,
      behavior: "smooth"
    });
  }, [messages.length, loading]);

  return (
    <div className="qk-chatbot-messages" ref={containerRef} aria-live="polite">
      {messages.map((message) => (
        <div key={message.id} className={`qk-chatbot-message qk-chatbot-message--${message.role}`}>
          {renderLinkedText(message.content)}
        </div>
      ))}
      {loading && (
        <div className="qk-chatbot-message qk-chatbot-message--assistant qk-chatbot-message--typing">
          <span />
          <span />
          <span />
        </div>
      )}
    </div>
  );
}

function renderLinkedText(content: string): ComponentChildren {
  const parts: ComponentChildren[] = [];
  const urlPattern = /(https?:\/\/[^\s<>"']+)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = urlPattern.exec(content)) !== null) {
    const url = trimTrailingPunctuation(match[0]);
    const start = match.index;
    const end = start + url.length;

    if (start > lastIndex) {
      parts.push(content.slice(lastIndex, start));
    }

    parts.push(
      <a key={`${url}_${start}`} href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
    );

    lastIndex = end;
  }

  if (lastIndex < content.length) {
    parts.push(content.slice(lastIndex));
  }

  return parts.length > 0 ? parts : content;
}

function trimTrailingPunctuation(url: string): string {
  return url.replace(/[.,;:!?)]$/g, "");
}
